/**
 * A type-ahead that finds a player by name, email or id.
 *
 * Built for the invoice composer, which needs a `userId` and used to ask for
 * one as a bare text field — a UUID nobody can type from memory, copied over
 * from the Players tab by hand, with a 404 as the only feedback that it was
 * copied wrong.
 *
 * ─── Three characters, because that is where the query stops being useless ──
 * `GET /admin/users?search=` is a `contains` over username and email, so one
 * character matches a large fraction of the population and two are not much
 * better. Below `MIN_CHARS` nothing is requested at all and the widget says
 * what it is waiting for, rather than flashing a list of the first twenty
 * accounts in the table on every keystroke.
 *
 * ─── Ids are found separately ───────────────────────────────────────────────
 * The search endpoint does NOT match on id — it filters `username` and `email`
 * only. Rather than change it, a value that LOOKS like an id is additionally
 * fetched straight from `GET /admin/users/:id` and, if that resolves, pinned to
 * the top of the results. A miss is silent: an id-shaped string that matches
 * nothing is simply not a result, which is the same answer the search gives.
 *
 * ─── Out-of-order responses ─────────────────────────────────────────────────
 * Typing "ada" fires after "ad" and can answer before it. Every query carries a
 * sequence number and anything that is not the latest is dropped on arrival —
 * without it the list settles on whichever request happened to be slowest,
 * which looks like a search that ignores the last letter typed.
 */
import { getUser, listUsers } from './admin-api';
import type { AdminUserRow } from './admin-api.types';
import { esc } from './admin-kit';

/** Below this, nothing is requested. Janeks: "give options starting with the 3rd character". */
export const MIN_CHARS = 3;

/** Keystrokes settle for this long before a request goes out. */
const DEBOUNCE_MS = 220;

/** More than this and the list stops being a shortlist. */
const MAX_RESULTS = 8;

/**
 * Is this worth trying as an id?
 *
 * Prisma's `@default(cuid())` — `c` followed by 24 base-36 characters. Kept
 * deliberately loose (length and alphabet, not the exact cuid grammar) so a
 * pasted id from a future id scheme still gets tried; the cost of a wrong guess
 * is one 404 that nothing renders.
 */
export function looksLikeId(value: string): boolean {
  return /^[a-z0-9]{16,40}$/i.test(value.trim());
}

export interface PlayerSearchOptions {
  /** Called when a player is chosen, and with `null` when the choice is cleared. */
  onPick(user: AdminUserRow | null): void;
  /** Pre-selected player, so the widget survives its host being repainted. */
  selected?: AdminUserRow | null;
  placeholder?: string;
}

/** Mounts the widget into `host` (which it takes over) and returns its teardown. */
export function mountPlayerSearch(host: HTMLElement, opts: PlayerSearchOptions): () => void {
  let live = true;
  let seq = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let results: AdminUserRow[] = [];
  let highlighted = -1;
  let selected: AdminUserRow | null = opts.selected ?? null;
  let message = '';

  host.classList.add('tg-adm-psearch');
  host.innerHTML = `
    <div class="tg-adm-psearch-chip" hidden></div>
    <input class="tg-adm-psearch-input" data-psearch-input autocomplete="off"
      spellcheck="false" placeholder="${esc(opts.placeholder ?? 'Name, email or ID')}"
      role="combobox" aria-expanded="false" aria-autocomplete="list">
    <div class="tg-adm-psearch-results" data-psearch-results role="listbox" hidden></div>`;

  const chip = host.querySelector<HTMLElement>('.tg-adm-psearch-chip')!;
  const input = host.querySelector<HTMLInputElement>('[data-psearch-input]')!;
  const list = host.querySelector<HTMLElement>('[data-psearch-results]')!;

  function label(user: AdminUserRow): string {
    return user.username || user.email;
  }

  function paintChip(): void {
    chip.hidden = !selected;
    input.hidden = !!selected;
    if (!selected) return;
    chip.innerHTML = `
      <span class="tg-adm-psearch-name">${esc(label(selected))}</span>
      <span class="tg-adm-mono">${esc(selected.email)}</span>
      <button type="button" class="tg-adm-btn" data-psearch-clear>Change</button>`;
  }

  function paintResults(): void {
    if (message) {
      list.hidden = false;
      list.innerHTML = `<p class="tg-adm-psearch-msg">${esc(message)}</p>`;
      input.setAttribute('aria-expanded', 'true');
      return;
    }
    if (!results.length) {
      list.hidden = true;
      list.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
      return;
    }
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    list.innerHTML = results.map((user, i) => `
      <button type="button" class="tg-adm-psearch-opt${i === highlighted ? ' tg-adm-psearch-on' : ''}"
        role="option" aria-selected="${i === highlighted}" data-psearch-pick="${esc(user.id)}">
        <span class="tg-adm-psearch-name">${esc(user.username || '—')}</span>
        <span class="tg-adm-mono">${esc(user.email)}</span>
        <span class="tg-adm-psearch-id tg-adm-mono">${esc(user.id)}</span>
      </button>`).join('');
  }

  function clearResults(): void {
    results = [];
    highlighted = -1;
    message = '';
    paintResults();
  }

  async function search(term: string): Promise<void> {
    const mine = ++seq;
    // The id lookup runs alongside the search rather than instead of it: a
    // 25-character string is id-SHAPED, not necessarily an id, and it could
    // equally be somebody's username.
    const [page, byId] = await Promise.all([
      listUsers({ search: term, status: 'all' }).catch(() => null),
      looksLikeId(term) ? getUser(term).catch(() => null) : Promise.resolve(null),
    ]);
    if (!live || mine !== seq) return;

    const merged: AdminUserRow[] = [];
    const seen = new Set<string>();
    for (const user of [byId, ...(page?.items ?? [])]) {
      if (!user || seen.has(user.id)) continue;
      seen.add(user.id);
      merged.push(user as AdminUserRow);
      if (merged.length >= MAX_RESULTS) break;
    }

    results = merged;
    highlighted = merged.length ? 0 : -1;
    message = merged.length
      ? ''
      : (page ? `Nobody matches "${term}".` : 'Could not reach the server.');
    paintResults();
  }

  function schedule(): void {
    if (timer) clearTimeout(timer);
    const term = input.value.trim();
    if (term.length < MIN_CHARS) {
      // Not an error — the field is simply not ready to be asked yet, and
      // saying so beats an empty box that looks broken.
      results = [];
      highlighted = -1;
      message = term.length ? `Keep typing — ${MIN_CHARS} characters minimum.` : '';
      paintResults();
      return;
    }
    timer = setTimeout(() => { void search(term); }, DEBOUNCE_MS);
  }

  function choose(user: AdminUserRow | null): void {
    selected = user;
    if (timer) { clearTimeout(timer); timer = null; }
    // Bumped so a request already in flight cannot repopulate the list behind
    // a choice that has already been made.
    seq += 1;
    clearResults();
    if (!user) { input.value = ''; }
    paintChip();
    if (!user) input.focus();
    opts.onPick(user);
  }

  const onInput = () => schedule();

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { clearResults(); return; }
    if (!results.length) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      highlighted = (highlighted + step + results.length) % results.length;
      paintResults();
      return;
    }
    if (e.key === 'Enter') {
      // The widget lives inside a <form>; without this, Enter on a highlighted
      // result submits the invoice instead of choosing the player.
      e.preventDefault();
      if (highlighted >= 0) choose(results[highlighted]!);
    }
  };

  const onHostClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('[data-psearch-clear]')) { choose(null); return; }
    const pick = target.closest<HTMLElement>('[data-psearch-pick]');
    if (!pick) return;
    const user = results.find((u) => u.id === pick.dataset.psearchPick);
    if (user) choose(user);
  };

  /** A click anywhere else closes the list, the way every other combobox does. */
  const onDocumentPointerDown = (e: Event) => {
    if (!host.contains(e.target as Node)) clearResults();
  };

  input.addEventListener('input', onInput);
  input.addEventListener('keydown', onKeyDown);
  host.addEventListener('click', onHostClick);
  document.addEventListener('pointerdown', onDocumentPointerDown);

  paintChip();

  return () => {
    live = false;
    if (timer) clearTimeout(timer);
    input.removeEventListener('input', onInput);
    input.removeEventListener('keydown', onKeyDown);
    host.removeEventListener('click', onHostClick);
    document.removeEventListener('pointerdown', onDocumentPointerDown);
  };
}
